import { Card, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceDot,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import useUnitProvider from '/@/core/context/unit';
import { _ } from '/@/locale';
import type { ManagementHybrid } from './use-management-data';

type ChartRow = { density: number } & Record<string, number>;

const AXIS_COLOR = 'rgba(16,56,79,0.12)';
const Y_AXIS_WIDTH = 56;

/**
 * Merges the per-hybrid curves onto a shared density axis so one hover reads every hybrid's
 * yield at that density, which is what the tooltip in the design shows. Densities come back
 * on the same grid for every product, so rows line up by value.
 */
const toChartRows = (hybrids: ManagementHybrid[]): ChartRow[] => {
    const byDensity = new Map<number, ChartRow>();
    hybrids.forEach((hybrid) => {
        hybrid.points.forEach((point) => {
            const row = byDensity.get(point.density) || ({ density: point.density } as ChartRow);
            row[String(hybrid.id)] = point.yield;
            byDensity.set(point.density, row);
        });
    });
    return [...byDensity.values()].sort((a, b) => a.density - b.density);
};

function DensityTooltip({
    active,
    payload,
    label,
    hybrids,
    formatDensity,
    formatYield,
}: {
    active?: boolean;
    payload?: { dataKey?: string | number; value?: number }[];
    label?: number;
    hybrids: ManagementHybrid[];
    formatDensity: (value: number) => string;
    formatYield: (value: number) => string;
}) {
    if (!active || !payload?.length || label === undefined) return null;

    return (
        <Card elevation={3} sx={{ p: 1.5, borderRadius: '4px' }}>
            <Stack spacing={0.5}>
                <Typography variant='caption' fontWeight={700} color='primary'>
                    {_('Densidad: {{density}}', { density: formatDensity(label) })}
                </Typography>
                {payload.map((entry) => {
                    const hybrid = hybrids.find(({ id }) => String(id) === String(entry.dataKey));
                    if (!hybrid || entry.value === undefined) return null;
                    return (
                        <Stack key={hybrid.id} direction='row' spacing={0.5} alignItems='center'>
                            <Dot color={hybrid.color} />
                            <Typography variant='caption' color='primary'>
                                {`${hybrid.catalogName}: ${formatYield(entry.value)}`}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        </Card>
    );
}

/** Lets the chart size itself from the viewport, or from an explicit width when there is none. */
function ChartFrame({ width, height, children }: { width?: number; height: number; children: React.ReactElement }) {
    if (width) return children;
    return (
        <ResponsiveContainer width='100%' height={height}>
            {children}
        </ResponsiveContainer>
    );
}

function Dot({ color }: { color: string }) {
    return <Stack sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />;
}

/** "Curva de densidad": estimated yield against harvest density, one line per hybrid. */
function DensityCurve({
    hybrids,
    height = 320,
    width,
}: {
    hybrids: ManagementHybrid[];
    height?: number;
    /**
     * Fixed chart width. Only the PDF passes it: `ResponsiveContainer` sizes itself by
     * measuring the DOM, and server-side rendering has no layout to measure, so the chart
     * would come out empty.
     */
    width?: number;
}) {
    const units = useUnitProvider();
    // The curve is ~180 points per hybrid; merging them is not work to redo on every render.
    const rows = useMemo(() => toChartRows(hybrids), [hybrids]);

    // Precision is left to the converter, which adapts to the user's density/yield units.
    // The tooltip carries units; the axes don't, since their titles already state them.
    const formatDensity = (value: number) => units.toCornDensity(value, undefined).toString();
    const formatYield = (value: number) => units.toCornYield(value, undefined).toString();
    const formatDensityTick = (value: number) => units.toCornDensity(value, undefined).localeValue;
    const formatYieldTick = (value: number) => units.toCornYield(value, undefined).localeValue;

    if (!rows.length) return null;

    return (
        <Card elevation={0} variant='outlined' sx={{ borderRadius: '8px', p: 2 }}>
            <Stack spacing={2}>
                <Stack>
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Curva de densidad')}
                    </Typography>
                </Stack>

                <Stack>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Rendimiento estimado')} ({units.getUnit('yield', 'corn')})
                    </Typography>
                    <ChartFrame width={width} height={height}>
                        <LineChart
                            data={rows}
                            width={width}
                            height={height}
                            margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
                        >
                            <CartesianGrid stroke={AXIS_COLOR} vertical={false} />
                            <XAxis
                                dataKey='density'
                                type='number'
                                domain={['dataMin', 'dataMax']}
                                tickFormatter={formatDensityTick}
                                tickLine={false}
                                axisLine={{ stroke: AXIS_COLOR }}
                                height={24}
                            />
                            {/*
                             * One shared Y axis for every hybrid: each <Line> binds to the default
                             * axis, and recharts unions their domains — so "multiple yields" needs
                             * no extra axes. `density` sits on the X axis and is excluded here.
                             * 'auto' lets recharts round the bounds so ticks land on whole numbers.
                             */}
                            <YAxis
                                type='number'
                                domain={['dataMin', 'auto']}
                                tickFormatter={formatYieldTick}
                                tickLine={false}
                                axisLine={{ stroke: AXIS_COLOR }}
                                width={Y_AXIS_WIDTH}
                            />

                            <Tooltip
                                content={
                                    <DensityTooltip
                                        hybrids={hybrids}
                                        formatDensity={formatDensity}
                                        formatYield={formatYield}
                                    />
                                }
                            />
                            {/*
                             * Each hybrid's recommended density, dropped as a dashed vertical
                             * line in its own colour. `ifOverflow='hidden'` keeps a stray value
                             * from stretching the axis domain.
                             */}
                            {hybrids.map((hybrid) =>
                                hybrid.harvestDensity ? (
                                    <ReferenceLine
                                        key={`recommended-${hybrid.id}`}
                                        stroke={hybrid.color}
                                        strokeDasharray='4 4'
                                        ifOverflow='hidden'
                                        segment={[
                                            { x: hybrid.harvestDensity, y: 0 },
                                            { x: hybrid.harvestDensity, y: hybrid.estimatedYield },
                                        ]}
                                    />
                                ) : null
                            )}
                            {/* Marks where each recommended density meets its curve. */}
                            {hybrids.map((hybrid) =>
                                hybrid.harvestDensity && hybrid.estimatedYield ? (
                                    <ReferenceDot
                                        key={`recommended-dot-${hybrid.id}`}
                                        x={hybrid.harvestDensity}
                                        y={hybrid.estimatedYield}
                                        r={4}
                                        fill={hybrid.color}
                                        stroke={hybrid.color}
                                        ifOverflow='hidden'
                                    />
                                ) : null
                            )}
                            {hybrids.map((hybrid) => (
                                <Line
                                    key={hybrid.id}
                                    type='monotone'
                                    dataKey={String(hybrid.id)}
                                    stroke={hybrid.color}
                                    strokeWidth={2}
                                    dot={false}
                                    activeDot={{ r: 4 }}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ChartFrame>

                    <Typography variant='caption' color='primary.mui.inactive' textAlign='right'>
                        {_('Densidad')} ({units.getUnit('density', 'corn')})
                    </Typography>
                </Stack>

                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap alignItems='center'>
                    <Typography variant='caption' color='primary.mui.inactive'>
                        {_('Híbridos seleccionados')}
                    </Typography>
                    {hybrids.map((hybrid) => (
                        <Stack key={hybrid.id} direction='row' spacing={0.5} alignItems='center'>
                            <Dot color={hybrid.color} />
                            <Typography variant='caption' color='primary'>
                                {hybrid.catalogName}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Card>
    );
}

export default DensityCurve;
